"use client";

import { useState, useMemo, type FC } from "react";
import { CheckIcon, CopyIcon, ChevronDownIcon, ChevronRightIcon } from "lucide-react";
import ShikiHighlighter from "react-shiki";
import { cn } from "@/lib/utils";

// =============================================================================
// TYPES
// =============================================================================

interface CodeBlockProps {
  data: unknown;
  title?: string;
  maxHeight?: string;
  defaultExpanded?: boolean;
  className?: string;
  language?: string;
}

interface ExtractedCode {
  key: string;
  value: string;
  language: string;
}

// =============================================================================
// LANGUAGE DETECTION
// =============================================================================

/**
 * Auto-detect language from content
 */
function detectLanguage(content: string): string {
  const trimmed = content.trim();

  // SQL: common keywords
  if (/^(SELECT|INSERT|UPDATE|DELETE|CREATE|ALTER|DROP|WITH|MERGE|TRUNCATE)\b/i.test(trimmed)) return "sql";
  if (/\b(FROM|WHERE|JOIN|GROUP BY|ORDER BY|HAVING|UNION)\b/i.test(trimmed) && /\bSELECT\b/i.test(trimmed)) return "sql";

  // Python: def, import, class, if __name__
  if (/^(def |import |from .+ import|class |if __name__|print\(|#.*python|async def )/im.test(trimmed)) return "python";
  if (/^\s*(def |class |import |from )/m.test(trimmed)) return "python";

  // JavaScript/TypeScript: const, let, var, function, =>
  if (/^(const |let |var |function |export |import .*from|=>|async function)/m.test(trimmed)) return "typescript";
  if (/\b(useState|useEffect|React|console\.log)\b/.test(trimmed)) return "typescript";

  // Shell: shebang or common commands
  if (/^(#!\/|curl |wget |npm |yarn |pip |apt |brew |\$ |echo |export |chmod |mkdir |cd |ls )/m.test(trimmed)) return "bash";

  // YAML: key: value pattern or starts with ---
  if (/^---/.test(trimmed)) return "yaml";
  if (/^[\w_-]+:\s*$/m.test(trimmed) || /^[\w_-]+:\s+[|\->]/m.test(trimmed)) return "yaml";

  // XML/HTML: starts with <
  if (/^<[!?]?\w/.test(trimmed)) return "xml";

  // JSON: starts with { or [
  if (/^[\{\[]/.test(trimmed)) return "json";

  // GraphQL
  if (/^(query|mutation|subscription|fragment)\s+\w+/i.test(trimmed)) return "graphql";

  // Regex pattern
  if (/^\/.*\/[gimsuvy]*$/.test(trimmed)) return "regex";

  // Default
  return "text";
}

/**
 * Check if detected language is "interesting" (not plain text)
 */
function isCodeLanguage(lang: string): boolean {
  return lang !== "text" && lang !== "json";
}

// =============================================================================
// CODE EXTRACTION
// =============================================================================

/**
 * Key patterns that likely contain code
 */
const CODE_KEY_PATTERNS = [
  /query/i, /sql/i,
  /code/i, /script/i, /snippet/i,
  /command/i, /cmd/i, /shell/i, /bash/i,
  /expression/i, /expr/i, /formula/i,
  /template/i, /pattern/i, /regex/i,
  /body/i, /payload/i, /content/i,
  /source/i, /src/i,
  /statement/i,
  /python/i, /javascript/i, /typescript/i,
  /html/i, /xml/i, /yaml/i,
  /request/i, /response/i,
  /graphql/i, /gql/i,
];

/**
 * Check if a key name suggests it contains code
 */
function isCodeKey(key: string): boolean {
  return CODE_KEY_PATTERNS.some(pattern => pattern.test(key));
}

/**
 * Check if a string value looks like code (multiline + detected as code)
 */
function looksLikeCode(value: string): boolean {
  if (typeof value !== "string") return false;
  const hasNewlines = value.includes("\n");
  const detectedLang = detectLanguage(value);
  return hasNewlines && isCodeLanguage(detectedLang);
}

/**
 * Extract code fields from top-level of object
 * Returns extracted code blocks and remaining data
 */
function extractCodeFields(data: unknown): { extracted: ExtractedCode[]; remaining: unknown } {
  if (typeof data !== "object" || data === null || Array.isArray(data)) {
    return { extracted: [], remaining: data };
  }

  const obj = data as Record<string, unknown>;
  const extracted: ExtractedCode[] = [];
  const remaining: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(obj)) {
    if (typeof value === "string" && value.trim()) {
      const shouldExtract = isCodeKey(key) || looksLikeCode(value);

      if (shouldExtract) {
        const language = detectLanguage(value);
        extracted.push({ key, value, language });
      } else {
        remaining[key] = value;
      }
    } else {
      remaining[key] = value;
    }
  }

  return { extracted, remaining };
}

/**
 * Check if remaining data is meaningful (not empty or trivial)
 */
function hasMeaningfulData(data: unknown): boolean {
  if (data === null || data === undefined) return false;
  if (typeof data !== "object") return true;
  if (Array.isArray(data)) return data.length > 0;
  return Object.keys(data as object).length > 0;
}

// =============================================================================
// HOOKS
// =============================================================================

function useCopyToClipboard(copiedDuration = 2000) {
  const [isCopied, setIsCopied] = useState(false);

  const copyToClipboard = (value: string) => {
    navigator.clipboard.writeText(value).then(() => {
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), copiedDuration);
    });
  };

  return { isCopied, copyToClipboard };
}

// =============================================================================
// SINGLE CODE BLOCK COMPONENT
// =============================================================================

interface SingleCodeBlockProps {
  code: string;
  language: string;
  title?: string;
  maxHeight?: string;
  defaultExpanded?: boolean;
  className?: string;
}

const SingleCodeBlock: FC<SingleCodeBlockProps> = ({
  code,
  language,
  title,
  maxHeight = "300px",
  defaultExpanded = false,
  className,
}) => {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const { isCopied, copyToClipboard } = useCopyToClipboard();

  const lines = code.split('\n');
  const needsCollapse = lines.length > 5;
  const preview = needsCollapse ? lines.slice(0, 5).join('\n') + '\n...' : null;
  const displayStr = expanded || !needsCollapse ? code : preview!;

  return (
    <div className={cn("rounded-lg border border-border bg-terminal-card overflow-hidden", className)}>
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-1.5 bg-muted/30 border-b border-border">
        <div className="flex items-center gap-2">
          {needsCollapse && (
            <button
              onClick={() => setExpanded(!expanded)}
              className="p-0.5 hover:bg-muted rounded transition-colors"
            >
              {expanded ? (
                <ChevronDownIcon className="w-3.5 h-3.5 text-muted-foreground" />
              ) : (
                <ChevronRightIcon className="w-3.5 h-3.5 text-muted-foreground" />
              )}
            </button>
          )}
          <span className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide">
            {title || language}
          </span>
        </div>
        <button
          onClick={() => copyToClipboard(code)}
          className="p-1 hover:bg-muted rounded transition-colors"
          title="Copy to clipboard"
        >
          {isCopied ? (
            <CheckIcon className="w-3.5 h-3.5 text-green-400" />
          ) : (
            <CopyIcon className="w-3.5 h-3.5 text-muted-foreground" />
          )}
        </button>
      </div>

      {/* Code content */}
      <div
        className="overflow-auto text-[11px] leading-relaxed [&>pre]:!bg-transparent [&>pre]:!p-3 [&>pre]:!m-0"
        style={{ maxHeight: expanded ? maxHeight : '120px' }}
      >
        <ShikiHighlighter language={language} theme="github-dark">
          {displayStr}
        </ShikiHighlighter>
      </div>

      {/* Expand hint */}
      {needsCollapse && !expanded && (
        <button
          onClick={() => setExpanded(true)}
          className="w-full px-3 py-1 text-[10px] text-muted-foreground hover:text-foreground hover:bg-muted/30 transition-colors border-t border-border"
        >
          Show all ({lines.length} lines)
        </button>
      )}
    </div>
  );
};

// =============================================================================
// MAIN COMPONENT - SMART CODE BLOCK
// =============================================================================

/**
 * CodeBlock - Smart code viewer that extracts and highlights code from JSON
 *
 * Features:
 * - Extracts code fields from JSON (query, sql, code, script, etc.)
 * - Auto-detects language for each extracted block
 * - Shows remaining JSON data separately
 * - Collapsible/expandable views
 * - Copy to clipboard
 */
export const CodeBlock: FC<CodeBlockProps> = ({
  data,
  title,
  maxHeight = "300px",
  defaultExpanded = false,
  className,
  language,
}) => {
  const { extracted, remaining } = useMemo(() => extractCodeFields(data), [data]);
  const hasRemaining = hasMeaningfulData(remaining);

  // If nothing extracted, show as single block with auto-detection
  if (extracted.length === 0) {
    const codeStr = typeof data === "string" ? data : JSON.stringify(data, null, 2);
    const detectedLang = language || detectLanguage(codeStr);

    return (
      <SingleCodeBlock
        code={codeStr}
        language={detectedLang}
        title={title}
        maxHeight={maxHeight}
        defaultExpanded={defaultExpanded}
        className={className}
      />
    );
  }

  // Show extracted code blocks + remaining JSON
  return (
    <div className={cn("space-y-2", className)}>
      {extracted.map((item, idx) => (
        <SingleCodeBlock
          key={`${item.key}-${idx}`}
          code={item.value}
          language={item.language}
          title={item.key}
          maxHeight={maxHeight}
          defaultExpanded={defaultExpanded || extracted.length === 1}
        />
      ))}

      {hasRemaining && (
        <SingleCodeBlock
          code={JSON.stringify(remaining, null, 2)}
          language="json"
          title="data"
          maxHeight={maxHeight}
          defaultExpanded={false}
        />
      )}
    </div>
  );
};

// Keep old name as alias for backward compatibility
export const JsonCodeBlock = CodeBlock;

// =============================================================================
// INLINE DISPLAY
// =============================================================================

export const JsonInline: FC<{ data: unknown; className?: string }> = ({ data, className }) => {
  const jsonStr = useMemo(() => {
    try {
      const str = JSON.stringify(data);
      if (str.length > 100) {
        return str.slice(0, 100) + '...';
      }
      return str;
    } catch {
      return String(data);
    }
  }, [data]);

  return (
    <code className={cn("text-[10px] text-muted-foreground font-mono", className)}>
      {jsonStr}
    </code>
  );
};
